import { useEffect, useRef, useState } from "react";
import type {
  ChangeEvent,
  ClipboardEvent,
  FocusEvent,
  FormEvent,
  KeyboardEvent,
  MouseEvent as ReactMouseEvent,
} from "react";
import { createPortal } from "react-dom";
import type { UseFormRegisterReturn } from "react-hook-form";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { Input, type InputProps } from "@/components/ui/input";
import {
  cleanBirthdateValue,
  formatToMMDDYYYY,
  isValidBirthdate,
  normalizeDOBInput,
  toCalendarDate,
} from "@/modules/add-new-data/lib/age";
import { cn } from "@/lib/utils";
import { useWorkspaceRoot } from "../../../web-adapter";

type DateOfBirthInputProps = Omit<InputProps, "onChange" | "onBlur" | "name" | "ref" | "value"> & {
  allowFutureDates?: boolean;
  calendarLabel?: string;
  registration: UseFormRegisterReturn;
  validationMode?: "birthdate" | "optional-date";
  value?: string | Date | null;
};

export function DateOfBirthInput({
  allowFutureDates = false,
  calendarLabel = "Choose date",
  registration,
  className,
  validationMode = "birthdate",
  value,
  ...props
}: DateOfBirthInputProps) {
  const workspaceRoot = useWorkspaceRoot();
  const visibleInputRef = useRef<HTMLInputElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [pendingCalendarValue, setPendingCalendarValue] = useState("");
  const [calendarMonth, setCalendarMonth] = useState(() => startOfMonth(new Date()));
  const [calendarError, setCalendarError] = useState<string | null>(null);
  const [popoverPosition, setPopoverPosition] = useState({ left: 0, top: 0 });

  useEffect(() => {
    if (!visibleInputRef.current) {
      return;
    }

    const normalizedValue = cleanDateInputValue(value);
    if (visibleInputRef.current.value !== normalizedValue) {
      visibleInputRef.current.value = normalizedValue;
    }
    syncCalendarState(normalizedValue);
  }, [value]);

  useEffect(() => {
    if (!isPickerOpen) {
      return;
    }

    function handleOutsidePointerDown(event: globalThis.MouseEvent) {
      const eventPath = event.composedPath();

      if (
        (wrapperRef.current && eventPath.includes(wrapperRef.current)) ||
        (popoverRef.current && eventPath.includes(popoverRef.current))
      ) {
        return;
      }

      setIsPickerOpen(false);
      setCalendarError(null);
    }

    document.addEventListener("mousedown", handleOutsidePointerDown);

    return () => document.removeEventListener("mousedown", handleOutsidePointerDown);
  }, [isPickerOpen]);

  useEffect(() => {
    if (!isPickerOpen) {
      return;
    }

    function handleViewportChange() {
      updatePopoverPosition();
    }

    updatePopoverPosition();
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);

    return () => {
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [isPickerOpen]);

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const input = event.currentTarget;

    if (!input) {
      return;
    }

    const formattedValue = normalizeDOBInput(input.value);

    setInputValue(input, formattedValue);
    input.setCustomValidity("");
    syncCalendarState(formattedValue);
    setCalendarError(null);
    notifyRegistrationChange(input);
  }

  function handleBeforeInput(event: FormEvent<HTMLInputElement>) {
    const nativeEvent = event.nativeEvent as InputEvent;

    if (!nativeEvent.data || /^[\d/-]+$/.test(nativeEvent.data)) {
      return;
    }

    event.preventDefault();
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Escape") {
      setIsPickerOpen(false);
      setCalendarError(null);
      return;
    }

    if (event.key === "Enter") {
      normalizeVisibleValue(event.currentTarget);
      return;
    }

    const isControlKey =
      event.ctrlKey ||
      event.metaKey ||
      event.altKey ||
      [
        "Backspace",
        "Delete",
        "Tab",
        "Enter",
        "ArrowLeft",
        "ArrowRight",
        "Home",
        "End",
      ].includes(event.key);

    if (isControlKey) {
      return;
    }

    if (!/^\d$|^\/$|^-$/.test(event.key)) {
      event.preventDefault();
    }
  }

  function handleBlur(event: FocusEvent<HTMLInputElement>) {
    normalizeVisibleValue(event.currentTarget);
    void registration.onBlur(event);
  }

  function handlePaste(event: ClipboardEvent<HTMLInputElement>) {
    event.preventDefault();

    const pastedValue = event.clipboardData.getData("text");
    const formattedValue = normalizeDOBInput(pastedValue);
    const nextValue = isValidBirthdate(formattedValue) ? cleanBirthdateValue(formattedValue) : formattedValue;

    const input = event.currentTarget;

    if (!input) {
      return;
    }

    setInputValue(input, nextValue);
    input.setCustomValidity("");
    syncCalendarState(nextValue);
    notifyRegistrationChange(input);
  }

  function handleCalendarClick(event?: ReactMouseEvent<HTMLButtonElement>) {
    event?.preventDefault();
    event?.stopPropagation();

    const visibleInput = visibleInputRef.current;
    const currentValue = visibleInput?.value ?? "";

    syncCalendarState(currentValue);
    setCalendarError(null);
    updatePopoverPosition();
    setIsPickerOpen(true);
  }

  function setRefs(input: HTMLInputElement | null) {
    visibleInputRef.current = input;
    registration.ref(input);
  }

  function updatePopoverPosition() {
    const input = visibleInputRef.current;

    if (!input) {
      return;
    }

    const rect = input.getBoundingClientRect();
    const width = 236;
    const height = 306;
    const left = Math.min(window.innerWidth - width - 8, Math.max(8, rect.right - width));
    const belowTop = rect.bottom + 6;
    const aboveTop = rect.top - height - 6;
    const top = belowTop + height > window.innerHeight && aboveTop > 8
      ? aboveTop
      : Math.min(window.innerHeight - height - 8, belowTop);

    setPopoverPosition({
      left,
      top: Math.max(8, top),
    });
  }

  function syncCalendarState(value: string) {
    const normalizedValue = cleanDateInputValue(value);
    const parsedDate = toCalendarDate(normalizedValue);

    if (parsedDate && (allowFutureDates || !isFutureCalendarDate(parsedDate))) {
      setPendingCalendarValue(normalizedValue);
      setCalendarMonth(startOfMonth(parsedDate));
      return;
    }

    setPendingCalendarValue("");
  }

  function normalizeVisibleValue(input: HTMLInputElement | null | undefined) {
    if (!input) {
      return;
    }

    const formattedValue = normalizeDOBInput(input.value);
    const normalizedValue =
      validationMode === "birthdate"
        ? isValidBirthdate(formattedValue)
          ? cleanBirthdateValue(formattedValue)
          : formattedValue
        : cleanDateInputValue(formattedValue);

    setInputValue(input, normalizedValue);
    syncCalendarState(normalizedValue);
    notifyRegistrationChange(input);
  }

  function notifyRegistrationChange(input: HTMLInputElement | null | undefined) {
    if (!input) {
      return;
    }

    const safeValue = typeof input.value === "string" ? input.value : "";

    void registration.onChange({
      target: { name: registration.name, value: safeValue },
      currentTarget: { name: registration.name, value: safeValue },
      type: "change",
    } as ChangeEvent<HTMLInputElement>);
  }

  function handleSelectCalendarDate(date: Date | null | undefined, event?: ReactMouseEvent<HTMLButtonElement>) {
    event?.preventDefault();
    event?.stopPropagation();

    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
      return;
    }

    if (!allowFutureDates && isFutureCalendarDate(date)) {
      return;
    }

    const formattedValue = formatToMMDDYYYY(date);
    const input = visibleInputRef.current;

    if (!formattedValue || !input) {
      return;
    }

    setInputValue(input, formattedValue);
    input.setCustomValidity("");
    setPendingCalendarValue(formattedValue);
    setCalendarError(null);
    notifyRegistrationChange(input);
    setIsPickerOpen(false);
  }

  function handleClearCalendarDate(event?: ReactMouseEvent<HTMLButtonElement>) {
    event?.preventDefault();
    event?.stopPropagation();

    const input = visibleInputRef.current;

    setPendingCalendarValue("");
    setCalendarError(null);

    if (!input) {
      setIsPickerOpen(false);
      return;
    }

    setInputValue(input, "");
    input.setCustomValidity("");
    notifyRegistrationChange(input);
    setIsPickerOpen(false);
  }

  function handleCalendarMonthChange(event: ChangeEvent<HTMLSelectElement>) {
    const monthValue = event.currentTarget?.value ?? "";
    const selectedMonth = Number(monthValue);

    if (!Number.isInteger(selectedMonth) || selectedMonth < 0 || selectedMonth > 11) {
      return;
    }

    setCalendarMonth((month) =>
      clampCalendarMonth(new Date(month.getFullYear(), selectedMonth, 1), allowFutureDates),
    );
  }

  function handleCalendarYearChange(event: ChangeEvent<HTMLSelectElement>) {
    const yearValue = event.currentTarget?.value ?? "";
    const year = Number(yearValue);

    if (!Number.isInteger(year)) {
      return;
    }

    setCalendarMonth((month) => clampCalendarMonth(new Date(year, month.getMonth(), 1), allowFutureDates));
  }

  const calendarDays = getCalendarDays(calendarMonth);
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  const canGoToNextMonth = allowFutureDates || addMonths(calendarMonth, 1) <= startOfMonth(new Date());
  const yearOptions = getYearOptions(currentYear, allowFutureDates ? 10 : 0);

  return (
    <div ref={wrapperRef} className="relative">
      <Input
        {...props}
        autoComplete="off"
        className={cn("pr-7", className)}
        inputMode="numeric"
        maxLength={10}
        name={registration.name}
        onBeforeInput={handleBeforeInput}
        onBlur={handleBlur}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        ref={setRefs}
      />
      <button
        type="button"
        aria-label={calendarLabel}
        className="absolute right-1 top-1/2 grid size-6 -translate-y-1/2 place-items-center rounded text-slate-500 hover:bg-slate-100 hover:text-emerald-800 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-emerald-700"
        disabled={props.disabled}
        onClick={handleCalendarClick}
        onMouseDown={(event) => event.preventDefault()}
      >
        <CalendarDays className="size-3.5" aria-hidden="true" />
      </button>
      {isPickerOpen && !props.disabled ? createPortal(
        <div
          ref={popoverRef}
          className="fixed z-[9999] w-[236px] rounded border border-slate-200 bg-white p-2 text-xs shadow-xl"
          style={{ left: popoverPosition.left, top: popoverPosition.top }}
        >
          <div className="mb-2 flex items-center justify-between gap-1">
            <button
              type="button"
              aria-label="Previous month"
              className="grid size-7 place-items-center rounded text-slate-500 hover:bg-slate-100 hover:text-slate-900"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setCalendarMonth((month) => addMonths(month, -1));
              }}
              onMouseDown={(event) => event.preventDefault()}
            >
              <ChevronLeft className="size-4" aria-hidden="true" />
            </button>
            <div className="flex min-w-0 flex-1 items-center justify-center gap-1">
              <select
                aria-label="Birth month"
                className="h-7 min-w-0 rounded border border-slate-300 bg-white px-1 text-xs font-medium text-slate-900 outline-none focus-visible:ring-1 focus-visible:ring-emerald-700"
                value={calendarMonth.getMonth()}
                onChange={handleCalendarMonthChange}
              >
                {monthOptions.map((month, index) => (
                  <option
                    key={month}
                    value={index}
                    disabled={!allowFutureDates && calendarMonth.getFullYear() === currentYear && index > currentMonth}
                  >
                    {month}
                  </option>
                ))}
              </select>
              <select
                aria-label="Birth year"
                className="h-7 w-16 rounded border border-slate-300 bg-white px-1 text-center text-xs font-medium text-slate-900 outline-none focus-visible:ring-1 focus-visible:ring-emerald-700"
                value={calendarMonth.getFullYear()}
                onChange={handleCalendarYearChange}
              >
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              aria-label="Next month"
              className="grid size-7 place-items-center rounded text-slate-500 hover:bg-slate-100 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-40"
              disabled={!canGoToNextMonth}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setCalendarMonth((month) => addMonths(month, 1));
              }}
              onMouseDown={(event) => event.preventDefault()}
            >
              <ChevronRight className="size-4" aria-hidden="true" />
            </button>
          </div>
          <div className="grid grid-cols-7 gap-0.5 text-center text-[10px] font-semibold uppercase text-slate-500">
            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
              <div key={day}>{day}</div>
            ))}
          </div>
          <div className="mt-1 grid grid-cols-7 gap-0.5">
            {calendarDays.map((date, index) => {
              if (!date) {
                return <div key={`blank-${index}`} className="size-7" />;
              }

              const displayValue = formatToMMDDYYYY(date);
              const isSelected = pendingCalendarValue === displayValue;
              const isFuture = !allowFutureDates && isFutureCalendarDate(date);

              return (
                <button
                  key={displayValue}
                  type="button"
                  className={cn(
                    "grid size-7 place-items-center rounded text-xs text-slate-700 hover:bg-slate-100 hover:text-slate-950",
                    isSelected && "bg-emerald-700 font-semibold text-white hover:bg-emerald-800 hover:text-white",
                    isFuture && "cursor-not-allowed text-slate-300 hover:bg-transparent hover:text-slate-300",
                  )}
                  disabled={isFuture}
                  onClick={(event) => handleSelectCalendarDate(date, event)}
                  onMouseDown={(event) => event.preventDefault()}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>
          {calendarError ? (
            <p className="mt-2 rounded bg-red-50 px-2 py-1 text-[11px] font-medium text-red-700">
              {calendarError}
            </p>
          ) : null}
          <div className="mt-2 flex items-center justify-end border-t border-slate-100 pt-2">
            <button
              type="button"
              className="rounded px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-950"
              onClick={handleClearCalendarDate}
              onMouseDown={(event) => event.preventDefault()}
            >
              Clear
            </button>
          </div>
        </div>,
        workspaceRoot ?? document.body,
      ) : null}
    </div>
  );
}

export const SafeDateInput = DateOfBirthInput;

function setInputValue(input: HTMLInputElement, value: string) {
  input.value = value;
}

function cleanDateInputValue(value: string | Date | null | undefined) {
  if (value instanceof Date) {
    return formatToMMDDYYYY(value);
  }

  const trimmedValue = String(value ?? "").trim();
  const isoDateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmedValue);

  if (isoDateMatch) {
    return `${isoDateMatch[2]}/${isoDateMatch[3]}/${isoDateMatch[1]}`;
  }

  return cleanBirthdateValue(trimmedValue);
}

function startOfMonth(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addMonths(date: Date, amount: number) {
  return new Date(date.getFullYear(), date.getMonth() + amount, 1);
}

function getCalendarDays(monthDate: Date) {
  const firstDay = startOfMonth(monthDate);
  const daysInMonth = new Date(firstDay.getFullYear(), firstDay.getMonth() + 1, 0).getDate();
  const leadingBlankCount = firstDay.getDay();
  const days: Array<Date | null> = Array.from({ length: leadingBlankCount }, () => null);

  for (let day = 1; day <= daysInMonth; day += 1) {
    days.push(new Date(firstDay.getFullYear(), firstDay.getMonth(), day));
  }

  while (days.length % 7 !== 0) {
    days.push(null);
  }

  return days;
}

function isFutureCalendarDate(date: Date) {
  const today = new Date();
  const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  return date > todayDate;
}

function clampCalendarMonth(date: Date, allowFutureDates = false) {
  const today = new Date();
  const currentMonth = startOfMonth(today);
  const earliestMonth = new Date(1900, 0, 1);

  if (!allowFutureDates && date > currentMonth) {
    return currentMonth;
  }

  if (date < earliestMonth) {
    return earliestMonth;
  }

  return startOfMonth(date);
}

const monthOptions = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function getYearOptions(currentYear: number, futureYears = 0) {
  const years: number[] = [];

  for (let year = currentYear + futureYears; year >= 1900; year -= 1) {
    years.push(year);
  }

  return years;
}
